import React from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { CardWrapper, CardHeader } from 'components'
import Button from 'components/Button'
import DataGrid, { ColumnDataGrid } from 'components/DataGrid'
import { addModal } from 'features/modals/modalSlice'
import { GetAdminsQuery, GetAdminsQueryVariables } from 'generated/graphql'
import { PickNode } from 'graphql/types'
import { getNodes } from 'graphql/utils'
import { useQuery, useEventSlug } from 'hooks'
import { _, colors } from 'utils'
import { eventSelector } from 'features/events/eventSlice'
import { getAdminsQuery } from './graphql/query'
import AdminRow from './AdminRow'
import InviteAdminModal from './InviteAdminModal'

const S = {
    TopToolbar: styled.div`
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        border-bottom: 1px solid ${colors.gray1};
    `
}

export type AdminType = PickNode<GetAdminsQuery, 'admins'>

const EventAdmins = () => {
    const eventSlug = useEventSlug()
    const dispatch = useDispatch()
    const { data, requestStatus, fetchData } = useQuery<
        GetAdminsQuery, GetAdminsQueryVariables
    >(getAdminsQuery, {
        eventId: eventSlug
    })
    const admins = getNodes(data?.event?.admins)
    const eventData = useSelector(eventSelector)
    const reachedLimit = admins.length >= eventData?.maxOwners

    const columns: ColumnDataGrid[] = [
        {
            name: 'info',
            width: 12,
            render: (admin: AdminType) => (
                <AdminRow admin={admin} callGetAdmins={fetchData} />
            )
        }
    ]

    const showInviteModal = () => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <InviteAdminModal
                    close={close}
                    callGetAdmins={fetchData}
                />
            ),
            header: _('event_admins.invite_modal_header'),
        }))
    }

    return (
        <CardWrapper style={{ padding: 0, overflow: 'hidden' }}>
            <S.TopToolbar>
                <CardHeader>
                    {_('event_admins.list_header')}
                </CardHeader>
                <Button
                    icon="plusBox"
                    size="sm"
                    onClick={showInviteModal}
                    disabled={reachedLimit}
                    tooltip={reachedLimit && _('event_admins.reached_limit')}
                >
                    {_('event_admins.invite_admin')}
                </Button>
            </S.TopToolbar>
            <DataGrid
                theme="light"
                columns={columns}
                data={admins}
                hideHeader
                hideAction
                requestStatus={requestStatus}
            />
        </CardWrapper>
    )
}

export default EventAdmins
